import { Capacitor } from '@capacitor/core'
import { t } from '@lingui/macro'
import { Suspense, useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { NativePlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { breakpoints } from '@/config/layout'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { ContentPagePaddingCSS } from '@/layout/components/content-page-layout/ContentPageLayout.styled'
import { DocumentExternalLinkSection } from '../../components/document-external-link-section/DocumentExternalLinkSection'
import { usePatientDocuments } from '../../hooks/usePatientDocuments'

/**
 * This is the root component for the documents module.
 * It will redirect to the appropriate overview based on the screen type.
 * @returns
 */
export const DocumentsRoot = () => {
  const isMaxTablet = useMediaQuery(breakpoints.toS)
  const isMobile = Capacitor.isNativePlatform() || isMaxTablet

  const navigate = useNavigate()
  const location = useLocation()

  const isDocumentRoot = location.pathname.endsWith('/documents')

  const { data: documentsResponse } = usePatientDocuments({ groupBy: 'year' })

  const unreadFiles = useMemo(() => {
    return documentsResponse?.meta?.openCount ?? 0
  }, [documentsResponse?.meta?.openCount])

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (isDocumentRoot && !isMobile) {
      navigate('/documents/personal')
    }
  }, [location, isMobile, navigate, isDocumentRoot])

  // Render
  if (isDocumentRoot && isMobile) {
    return (
      <ContentPageLayout
        noPadding
        animationkey='documents-root'
        header={
          <Suspense fallback={null}>
            <NativePlatform>
              <StMobileHeader>
                <AppHeader
                  enableNotifications
                  profileButtonEnabled
                  centered={false}
                  title={t({
                    id: 'documents.title',
                    message: 'Documenten',
                  })}
                />
              </StMobileHeader>
            </NativePlatform>
          </Suspense>
        }
      >
        <StContentContainer>
          <DocumentExternalLinkSection unreadFiles={unreadFiles} />
        </StContentContainer>
      </ContentPageLayout>
    )
  }

  return <Outlet />
}

export const StContainer = styled(Flex)`
  display: flex;
  width: 100%;
  height: 100%;
`

export const StColumnContainer = styled(FlexColumn)`
  display: flex;
  flex: 1;
  flexshrink: 0;
  width: 100%;
`

const StMobileHeader = styled.div`
  ${ContentPagePaddingCSS}
  padding-bottom: 0;
`

const StContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space8};
  ${ContentPagePaddingCSS}

  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space4};
`
